/**
 * Estado do módulo Pedidos: a obra escolhida e o carrinho dela.
 *
 * A OBRA É O CONTEXTO DE TUDO AQUI. Decisão do Lucas (03/08): um carrinho por
 * obra, sempre aberto. Então escolher a obra não é um campo do formulário
 * final — é a primeira coisa, e fica visível o tempo todo no topo, como o CEP
 * de entrega no Mercado Livre: muda o que "adicionar" significa.
 *
 * A obra é perguntada A CADA VISITA, e não lembrada — ver `obraAtual`.
 */

import { api } from '../../api.js';

export interface ObraEscolhida {
  codigo: string;
  nome: string;
}

/**
 * A obra vale só para ESTA visita — não é lembrada entre sessões.
 *
 * A primeira versão guardava no `localStorage`, e o Lucas mandou tirar: quem
 * pede material trabalha para várias obras, e voltar já dentro da última é
 * como o pedido vai parar na obra errada. Perguntar toda vez custa um toque;
 * material entregue no endereço errado custa uma viagem.
 */
let obra: ObraEscolhida | null = null;

export function obraAtual(): ObraEscolhida | null {
  return obra;
}

export function escolherObra(escolhida: ObraEscolhida): void {
  consulta = false;
  obra = escolhida;
}

/**
 * MODO CONSULTA — entrar sem escolher obra, só para olhar o catálogo.
 *
 * Pedido do Lucas (04/08). Existe porque a pergunta mais comum na vitrine não
 * é "quero pedir", é "temos isso?". Obrigar a escolher obra para responder
 * isso é atrito num caminho que nem termina em pedido.
 *
 */
let consulta = false;

export function entrarEmConsulta(): void {
  consulta = true;
  obra = null;
}

export function emConsulta(): boolean {
  return consulta;
}

/**
 * O item que a pessoa quis adicionar estando em consulta.
 *
 * Guardado enquanto ela escolhe a obra, e adicionado ao carrinho assim que
 * escolher. É o que faz "quero este" não virar "escolha a obra e procure de
 * novo" — o pedido do Lucas foi exatamente esse: levar de volta à escolha e,
 * feita a escolha, o produto entra.
 */
let pendente: { codigo: string; quantidade: number } | null = null;

export function guardarPendente(codigo: string, quantidade: number): void {
  pendente = { codigo, quantidade };
}

/** Só pergunta se existe — a tela de obra usa isto para saber o que dizer. */
export function temPendente(): boolean {
  return pendente !== null;
}

export function consumirPendente(): { codigo: string; quantidade: number } | null {
  const p = pendente;
  pendente = null;
  return p;
}

export interface ItemCarrinho {
  codigo: string;
  nome: string;
  unidade: string | null;
  saldo: number;
  quantidade: number;
  foto: string | null;
  miniatura: string | null;
  /** Ferramenta com exemplares: o envio exige o prazo de reserva. */
  controlaUnidades: boolean;
  reservadoAte: string | null;
}

export interface Carrinho {
  id: number;
  obra: string;
  obraNome: string;
  itens: ItemCarrinho[];
}

/**
 * O que a lista importada NÃO achou, esperando virar observação do pedido.
 *
 * Decisão do Lucas: o não-encontrado fica de fora do pedido, só reportado. Mas
 * reportar apenas na tela morre quando a tela fecha — então a lista deixa
 * o registro aqui, e o carrinho o incorpora à observação no envio. Assim o CD
 * lê "vieram na lista e não temos: ..." no próprio pedido.
 */
let naoEncontrados: string[] = [];

export function registrarNaoEncontrados(linhas: string[]): void {
  naoEncontrados = linhas;
}

/** Lê e limpa — é consumo único, do envio do pedido. */
export function consumirNaoEncontrados(): string[] {
  const l = naoEncontrados;
  naoEncontrados = [];
  return l;
}

/** Uma assinatura simples para o topo saber quando o carrinho mudou. */
type Ouvinte = (c: Carrinho) => void;
const ouvintes: Ouvinte[] = [];

export function aoMudarCarrinho(fn: Ouvinte): void {
  ouvintes.push(fn);
}

/**
 * O carrinho aberto, guardado no módulo. Cada mutação devolve os itens novos,
 * então uma chamada basta — o GET completo só acontece ao trocar de obra.
 */
let aberto: Carrinho | null = null;

function atualizar(itens: ItemCarrinho[]): Carrinho {
  aberto = { ...aberto!, itens };
  for (const fn of ouvintes) fn(aberto);
  return aberto;
}

export async function carregarCarrinho(obra: string): Promise<Carrinho> {
  aberto = await api.get<Carrinho>(`/api/carrinho/${encodeURIComponent(obra)}`);
  for (const fn of ouvintes) fn(aberto);
  return aberto;
}

export async function adicionar(obra: string, codigo: string, quantidade: number): Promise<Carrinho> {
  // Garante que o carrinho existe — o GET é quem cria. Só na primeira vez.
  if (!aberto || aberto.obra !== obra) await carregarCarrinho(obra);
  const r = await api.post<{ itens: ItemCarrinho[] }>(
    `/api/carrinho/${encodeURIComponent(obra)}/itens`,
    { codigo, quantidade },
  );
  return atualizar(r.itens);
}

export async function mudarQuantidade(
  obra: string,
  codigo: string,
  quantidade: number,
): Promise<Carrinho> {
  const r = await api.put<{ itens: ItemCarrinho[] }>(
    `/api/carrinho/${encodeURIComponent(obra)}/itens/${encodeURIComponent(codigo)}`,
    { quantidade },
  );
  return atualizar(r.itens);
}

/** O prazo da ferramenta: "até quando pretende usar". */
export async function mudarReserva(
  obra: string,
  codigo: string,
  reservadoAte: string,
): Promise<Carrinho> {
  const r = await api.put<{ itens: ItemCarrinho[] }>(
    `/api/carrinho/${encodeURIComponent(obra)}/itens/${encodeURIComponent(codigo)}`,
    { reservadoAte },
  );
  return atualizar(r.itens);
}

export async function tirar(obra: string, codigo: string): Promise<Carrinho> {
  const r = await api.del<{ itens: ItemCarrinho[] }>(
    `/api/carrinho/${encodeURIComponent(obra)}/itens/${encodeURIComponent(codigo)}`,
  );
  return atualizar(r.itens);
}
